import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger.js";

gsap.registerPlugin(ScrollTrigger);

const INTRO_STORAGE_KEY = "introPlayed";

function hasPlayedIntro() {
    try {
        return sessionStorage.getItem(INTRO_STORAGE_KEY) === "true";
    } catch (error) {
        return false;
    }
}

function rememberIntro() {
    try {
        sessionStorage.setItem(INTRO_STORAGE_KEY, "true");
    } catch (error) {
        // Storage can be unavailable in private browsing modes.
    }
}

function splitCopyIntoWords(container) {
    if (!container) return [];

    const walker = document.createTreeWalker(
        container,
        NodeFilter.SHOW_TEXT,
        {
            acceptNode: (node) => node.nodeValue.trim()
                ? NodeFilter.FILTER_ACCEPT
                : NodeFilter.FILTER_REJECT,
        }
    );
    const textNodes = [];

    while (walker.nextNode()) textNodes.push(walker.currentNode);

    textNodes.forEach((textNode) => {
        const fragment = document.createDocumentFragment();
        const parts = textNode.nodeValue.split(/(\s+)/);

        parts.forEach((part) => {
            if (!part) return;

            if (/^\s+$/.test(part)) {
                fragment.appendChild(document.createTextNode(part));
                return;
            }

            const clip = document.createElement("span");
            const word = document.createElement("span");
            clip.className = "intro-word-clip";
            word.className = "intro-word";
            word.textContent = part;
            clip.appendChild(word);
            fragment.appendChild(clip);
        });

        textNode.parentNode.replaceChild(fragment, textNode);
    });

    return gsap.utils.toArray(container.querySelectorAll(".intro-word"));
}

function getActiveVimeoPlayer(section) {
    const players = gsap.utils.toArray(section.querySelectorAll("[data-intro-player]"));
    return players.find((player) => window.getComputedStyle(player).display !== "none") || null;
}

function setVimeoTriggerState(trigger, isPlaying) {
    if (!trigger) return;

    const playIcon = trigger.querySelector("[data-video-trigger-play]");
    const pauseIcon = trigger.querySelector("[data-video-trigger-pause]");
    const playLabel = trigger.dataset.playLabel || "Play video";
    const pauseLabel = trigger.dataset.pauseLabel || "Pause video";
    const label = isPlaying ? pauseLabel : playLabel;

    trigger.classList.toggle("is-playing", isPlaying);
    trigger.setAttribute("aria-pressed", isPlaying ? "true" : "false");
    trigger.setAttribute("aria-label", label);
    trigger.setAttribute("title", label);

    if (playIcon) playIcon.hidden = isPlaying;
    if (pauseIcon) pauseIcon.hidden = !isPlaying;
}

const VIMEO_ORIGIN = "https://player.vimeo.com";

function postVimeoCommand(player, method, value) {
    if (!player?.contentWindow) return false;

    const src = player.getAttribute("src");
    if (!src) return false;

    try {
        if (new URL(src, document.baseURI).origin !== VIMEO_ORIGIN) {
            return false;
        }
    } catch {
        return false;
    }

    // A readable document means the iframe is still same-origin,
    // e.g. its initial about:blank document or a consent placeholder.
    if (player.contentDocument !== null) return false;

    const message = { method };

    if (value !== undefined) {
        message.value = value;
    }

    player.contentWindow.postMessage(message, VIMEO_ORIGIN);
    return true;
}

function getVimeoPlaybackStateFromMessage(event, player) {
    if (event.origin !== "https://player.vimeo.com" || event.source !== player?.contentWindow) return null;

    let data = event.data;
    if (typeof data === "string") {
        try {
            data = JSON.parse(data);
        } catch (error) {
            return null;
        }
    }

    if (!data) return null;
    if (data.event === "play") return true;
    if (data.event === "pause" || data.event === "finish") return false;
    if (data.method === "getPaused" && typeof data.value === "boolean") return !data.value;

    return null;
}

function waitForVimeoPlayback(section) {
    const player = getActiveVimeoPlayer(section);

    if (!player || !player.contentWindow) return Promise.resolve();

    return new Promise((resolve) => {
        let finished = false;
        const fallback = window.setTimeout(finish, 2000);

        function finish() {
            if (finished) return;
            finished = true;
            window.clearTimeout(fallback);
            window.removeEventListener("message", onMessage);
            resolve();
        }

        function onMessage(event) {
            if (getVimeoPlaybackStateFromMessage(event, player)) {
                finish();
            }
        }

        function listenForPlayback() {
            player.contentWindow.postMessage({ method: "addEventListener", value: "play" }, "https://player.vimeo.com");
            player.contentWindow.postMessage({ method: "getPaused" }, "https://player.vimeo.com");
        }

        window.addEventListener("message", onMessage);
        player.addEventListener("load", listenForPlayback, { once: true });
        listenForPlayback();
    });
}

function dm_vah_01_homepage_header() {
    const section = document.querySelector("section.vah-01-homepage-header");
    const overlay = section?.querySelector(".homepage-intro-overlay");
    const videoTrigger = section?.querySelector(".video-trigger");

    if (!section || !document.body.classList.contains("home")) return;

    const header = document.querySelector("#nav-header");
    const headerInner = header?.querySelector(".header-inner");
    const logo = header?.querySelector(".header-logo");
    const navItems = header ? gsap.utils.toArray(header.querySelectorAll(
        ".header-menu > ul > li, .header-nav > .btn, .nav-toggle, .nav-search"
    )) : [];
    const video = section.querySelector(".video");
    const buttons = gsap.utils.toArray(section.querySelectorAll(".content > .btn"));
    const animatedElements = [video, logo, ...navItems, ...buttons].filter(Boolean);
    let isPlaying = true;

    setVimeoTriggerState(videoTrigger, isPlaying);

    const updateTriggerFromPlayer = (event) => {
        const player = getActiveVimeoPlayer(section);
        const nextState = getVimeoPlaybackStateFromMessage(event, player);

        if (nextState === null) return;

        isPlaying = nextState;
        setVimeoTriggerState(videoTrigger, isPlaying);
    };

    window.addEventListener("message", updateTriggerFromPlayer);

    const autoplayActivePlayer = () => {
        postVimeoCommand(getActiveVimeoPlayer(section), "play");
    };

    gsap.utils.toArray(section.querySelectorAll("[data-intro-player]")).forEach((player) => {
        player.addEventListener("load", autoplayActivePlayer, { once: true });
    });
    autoplayActivePlayer();

    if (videoTrigger) {
        videoTrigger.addEventListener("click", (event) => {
            event.preventDefault();

            const player = getActiveVimeoPlayer(section);
            if (!player) return;

            if (isPlaying) {
                postVimeoCommand(player, "pause");
                isPlaying = false;
            } else {
                postVimeoCommand(player, "play");
                isPlaying = true;
            }

            setVimeoTriggerState(videoTrigger, isPlaying);
        });
    }

    const revealImmediately = () => {
        const wasIntroPlaying = document.body.classList.contains("homepage-intro-playing");

        document.body.classList.remove("homepage-intro-playing");
        overlay?.remove();
        gsap.set(animatedElements, { clearProps: "all" });
        if (headerInner) gsap.set(headerInner, { clearProps: "--intro-nav-gradient-opacity" });

        if (wasIntroPlaying) requestAnimationFrame(() => ScrollTrigger.refresh());
    };

    if (hasPlayedIntro() || window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
        rememberIntro();
        revealImmediately();
        return;
    }

    document.body.classList.add("homepage-intro-playing");

    const words = splitCopyIntoWords(section.querySelector(".intro-copy"));
    gsap.set(video, { autoAlpha: 0 });
    gsap.set(logo, { autoAlpha: 0, y: -24 });
    gsap.set(navItems, { autoAlpha: 0, y: -16 });
    gsap.set(words, { yPercent: 115 });
    gsap.set(buttons, { autoAlpha: 0, y: 20 });

    waitForVimeoPlayback(section).then(() => {
        isPlaying = true;
        setVimeoTriggerState(videoTrigger, isPlaying);

        const timeline = gsap.timeline({
            defaults: { ease: "power3.out" },
            onComplete: () => {
                rememberIntro();
                document.body.classList.remove("homepage-intro-playing");
                overlay?.remove();
                gsap.set([...animatedElements, ...words], { clearProps: "all" });
                if (headerInner) gsap.set(headerInner, { clearProps: "--intro-nav-gradient-opacity" });
                requestAnimationFrame(() => ScrollTrigger.refresh());
            },
        });

        if (video) timeline.to(video, { autoAlpha: 1, duration: 1.4 }, 0);
        timeline.to(overlay, { autoAlpha: 0, duration: 1.4 }, 0);
        if (logo) timeline.to(logo, { autoAlpha: 1, y: 0, duration: 0.8 }, 0.65);
        if (headerInner) timeline.to(headerInner, { "--intro-nav-gradient-opacity": 1, duration: 1 }, 0.85);
        if (navItems.length) timeline.to(navItems, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.06 }, 0.9);
        if (words.length) timeline.to(words, { yPercent: 0, duration: 0.75, stagger: 0.035 }, 1.25);
        if (buttons.length) timeline.to(buttons, { autoAlpha: 1, y: 0, duration: 0.7, stagger: 0.1 }, "-=0.35");
    }).catch(revealImmediately);
}

export { dm_vah_01_homepage_header };
